import {
  getAuthToken,
  post,
  removeAuthToken,
  setAuthToken,
} from '@micun/web-components'
import { isString } from 'lodash'
import qs from 'qs'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { getBaseOrigin } from 'utils/common'
import { proxy, useSnapshot } from 'valtio'

export const authBaseUrl = getBaseOrigin('sso')

type CheckAuthResult = {
  login: boolean
  user: api.sso.User | null
}

export const checkAuthority = async () => {
  const saToken = getAuthToken()
  if (saToken) {
    console.log('auth: token 存在')
  } else {
    console.log('auth: token 不存在, 跳转登录页')
  }
  try {
    const { data } = await post<CheckAuthResult>({
      url: `${authBaseUrl}/sso/isLogin`,
      headers: {
        satoken: saToken,
      },
    })
    const { login: isAuthed, user } = data
    if (saToken && !isAuthed) {
      console.log('auth: token 无效, 跳转登录页')
    }
    if (user) {
      console.log('auth: 当前用户', user)
    }
    return { isAuthed, user }
  } catch (e) {
    console.error(e)
    return { isAuthed: false, user: null }
  }
}

export const getSsoAuthUrl = async () => {
  const currentUrl = encodeURIComponent(window.location.href)
  const redirectUrl = encodeURIComponent(
    `${window.location.origin}/?back=${currentUrl}`
  )
  const { data } = await post<string>({
    url: `${authBaseUrl}/sso/getSsoLoginUrl?clientLoginUrl=${redirectUrl}`,
  })
  const ssoAuthUrl = data
  const qIndex = ssoAuthUrl.indexOf('?')
  const queryString = qIndex > -1 ? ssoAuthUrl.slice(qIndex + 1) : ''
  const searchQuery = queryString ? qs.parse(queryString) : {}
  return { url: ssoAuthUrl, searchQuery }
}

export const loginByTicket = async (ticket: string) => {
  const { data } = await post<string>({
    url: `${authBaseUrl}/sso/doLoginByTicket`,
    query: { ticket },
  })
  const nextSaToken = data
  return nextSaToken
}

export const queryLogout = async () => {
  const data = await post<string>({
    url: `${authBaseUrl}/sso/signout`,
    method: 'post',
  })

  return data
}

type AuthData = {
  fetched: boolean
  isAuthed: boolean
  user: api.sso.User | null
  error?: string
}

export const authStore = proxy<AuthData>({
  fetched: false,
  isAuthed: false,
  user: null,
  error: undefined,
})

export const useAuth = (navigate?: ReturnType<typeof useNavigate>) => {
  const { fetched, isAuthed, user, error } = useSnapshot(authStore)
  const searchQuery = useMemo(
    () => qs.parse(window.location.search.slice(1)),
    []
  )

  useEffect(() => {
    const { ticket, back } = searchQuery

    async function getToken() {
      console.group('auth')
      console.log('auth: auth init')
      const shouldGetToken = isString(ticket) && isString(back)
      if (shouldGetToken) {
        try {
          // 根据 ticket 获取 token
          const nextSaToken = await loginByTicket(ticket)
          console.log('auth: 存储 token')
          setAuthToken(nextSaToken)
          // 获取用户信息
          const authInfo = await checkAuthority()
          localStorage.setItem('user', JSON.stringify(authInfo.user))
          authStore.isAuthed = authInfo.isAuthed
          authStore.user = authInfo.user
          console.log('auth: back: ', back)
          if (typeof navigate === 'function') {
            const url = new URL(back)
            navigate(
              {
                pathname: url.pathname,
                search: url.search,
                hash: url.hash,
              },
              {
                replace: true,
              }
            )
          } else {
            window.location.href = back
          }
        } catch (e: any) {
          authStore.error = `token获取异常, 原因: ${e.message}`
        }
      } else {
        // auth 流程
        const { token } = searchQuery
        if (isString(token)) {
          setAuthToken(token)
        }
        // 获根据当前已有 token 判断是否未登录
        const authInfo = await checkAuthority()

        if (!authInfo.isAuthed) {
          try {
            const { url } = await getSsoAuthUrl()
            // 未登录跳转 sso 页面
            window.location.href = url
          } catch (e: any) {
            authStore.error = e.message
            console.error(e)
          }
        } else {
          authStore.isAuthed = authInfo.isAuthed
          authStore.user = authInfo.user
        }
      }
      authStore.fetched = true
      console.groupEnd()
    }

    if (!fetched && !isAuthed) {
      getToken()
    }
  }, [searchQuery, isAuthed, fetched, navigate])

  const handleLogout = async () => {
    try {
      await queryLogout()
      removeAuthToken()
      localStorage.removeItem('user')
      const { url } = await getSsoAuthUrl()
      window.location.href = url
    } catch (e: any) {
      console.error(e)
    }
  }

  return {
    fetched,
    error,
    isAuthed,
    user,
    handleLogout,
    logout: handleLogout,
  }
}
